import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import './OS_Page.css';
import { FolderIcon, TypewriterIcon, PaperIcon, TrashIcon, RobotIcon, TerminalIcon } from './SVGIcons';
import TextEditor from './TextEditor';
import Screen1 from './Screen1.js';
import seanImage from './sean.png';
import Chat from './Chat.js';
import ProjectsContent from './ProjectsContent';
import Terminal from './Terminal';
import RetroScene from './RetroScene';

// ── Live system telemetry (clock + fake CPU/MEM readout) ─────────────────────
const SystemStats = () => {
  const [time, setTime] = useState(new Date());
  const [cpu, setCpu] = useState(12);
  const [mem, setMem] = useState(38);

  useEffect(() => {
    const tick = setInterval(() => {
      setTime(new Date());
      // wander the fake load around so the readout feels alive
      setCpu(prev => Math.min(97, Math.max(4, prev + (Math.random() * 14 - 7))));
      setMem(prev => Math.min(92, Math.max(22, prev + (Math.random() * 6 - 3))));
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const hh = String(time.getHours()).padStart(2, '0');
  const mm = String(time.getMinutes()).padStart(2, '0');
  const ss = String(time.getSeconds()).padStart(2, '0');

  return (
    <div className="sys-stats">
      <span className="sys-stat">CPU {String(Math.round(cpu)).padStart(2, '0')}%</span>
      <span className="sys-stat">MEM {String(Math.round(mem)).padStart(2, '0')}%</span>
      <span className="sys-online"><span className="sys-online-dot" />ONLINE</span>
      <span className="sys-clock">{hh}:{mm}<span className="sys-clock-sec">:{ss}</span></span>
    </div>
  );
};

const MenuBar = () => (
  <div className="menu-bar">
    <div className="menu-bar-left">
      <span>File</span>
      <span>View</span>
      <span>Help</span>
    </div>
    <div className="menu-bar-center">
      <span className="sos-clover">☘</span>
      <span className="sos-title glitch-text" data-text="S.O.S">S.O.S</span>
    </div>
    <div className="menu-bar-right">
      <SystemStats />
    </div>
  </div>
);

// ── Desktop icon (drag-and-drop, desktop only) ────────────────────────────────
const DesktopIcon = ({ label, icon, onDoubleClick, onDragStart, onDragEnd, position }) => {
  const lastTap = React.useRef(0);
  const dragged = React.useRef(false);

  // Distinguish a tap from a drag so dragging doesn't open windows
  const handleDragStart = (e) => { dragged.current = false; if (onDragStart) onDragStart(e); };
  const handleDragStop  = (e, data) => { dragged.current = true; if (onDragEnd) onDragEnd(e, data); };

  // Touch: double-tap to open (mirrors mouse double-click)
  const handleTouchEnd = (e) => {
    if (dragged.current) return;
    const now = Date.now();
    if (now - lastTap.current < 300) onDoubleClick();
    lastTap.current = now;
  };

  return (
    <Draggable
      bounds="parent"
      onStart={handleDragStart}
      onStop={handleDragStop}
      defaultPosition={position}
    >
      <div
        className="desktop-icon"
        onDoubleClick={onDoubleClick}
        onTouchEnd={handleTouchEnd}
      >
        <div className="icon">{icon}</div>
        <div className="label">{label}</div>
      </div>
    </Draggable>
  );
};

// ── Mobile dock (fixed bottom bar) ───────────────────────────────────────────
const MobileDock = ({ items, onOpen }) => (
  <div className="mobile-dock">
    {items.map(item => (
      <button key={item.id} className="dock-item" onClick={() => onOpen(item.id)}>
        <div className="dock-item-icon">{item.icon}</div>
        <div className="dock-item-label">{item.label}</div>
      </button>
    ))}
  </div>
);

// ── Main OS component ─────────────────────────────────────────────────────────
const BASE_Z = 500;

const OS_Page = () => {
  const [openWindows, setOpenWindows] = useState({});
  const [zMap, setZMap] = useState({});
  const zCounter = useRef(BASE_Z);
  const [notes, setNotes] = useState([]);
  const [draggingNote, setDraggingNote] = useState(null);
  const [trashPosition, setTrashPosition] = useState({ x: window.innerWidth - 250, y: window.innerHeight - 300 });
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const sections = [
    { id: 'about',       label: 'About Me',  icon: <PaperIcon /> },
    { id: 'resume',      label: 'Resume',    icon: <PaperIcon /> },
    { id: 'projects',    label: 'Projects',  icon: <FolderIcon /> },
    { id: 'text-editor', label: 'Notes',     icon: <TypewriterIcon /> },
    { id: 'chat',        label: 'Chat',      icon: <RobotIcon /> },
    { id: 'terminal',    label: 'Terminal',  icon: <TerminalIcon /> },
  ];

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
      setTrashPosition(mobile
        ? { x: window.innerWidth - 120, y: window.innerHeight - 200 }
        : { x: window.innerWidth - 250, y: window.innerHeight - 300 }
      );
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Bring a window to the top of the stack
  const focusWindow = (id) => {
    setZMap(prev => {
      if (prev[id] === zCounter.current) return prev;
      zCounter.current += 1;
      return { ...prev, [id]: zCounter.current };
    });
  };

  const handleOpenWindow = (id) => {
    // cascade each new window so they don't stack dead-center
    const openCount = Object.values(openWindows).filter(Boolean).length;
    const offset = (openCount % 6) * 32;
    const position = isMobile
      ? { x: 0, y: 0 }
      : {
          x: Math.max(0, (windowSize.width  / 2) - 400 + offset),
          y: Math.max(0, (windowSize.height / 2) - 300 + offset * 0.7),
        };
    setOpenWindows(prev => ({ ...prev, [id]: prev[id] || position }));
    focusWindow(id);
  };

  const handleCloseWindow = (id) => {
    setOpenWindows(prev => ({ ...prev, [id]: false }));
  };

  const handleSaveNote = (fileName, content) => {
    const newNote = {
      id: Date.now(),
      label: fileName,
      content,
      position: { x: windowSize.width - 160, y: 100 + notes.length * 100 },
    };
    setNotes(prev => [...prev, newNote]);
    setOpenWindows(prev => ({ ...prev, 'text-editor': false }));
  };

  const handleNoteDragStart = (noteId) => setDraggingNote(noteId);

  const handleNoteDragEnd = (e, data) => {
    const tr = trashPosition;
    if (
      draggingNote &&
      data.x >= tr.x - 30 && data.x <= tr.x + 200 &&
      data.y >= tr.y - 30 && data.y <= tr.y + 200
    ) {
      setNotes(prev => prev.filter(n => n.id !== draggingNote));
      setOpenWindows(prev => ({ ...prev, [`note-${draggingNote}`]: false }));
    }
    setDraggingNote(null);
  };

  // Dock items = sections + any saved notes
  const dockItems = [
    ...sections,
    ...notes.map(n => ({ id: `note-${n.id}`, label: n.label, icon: <PaperIcon /> })),
  ];

  return (
    <div className="classic-mac">
      <RetroScene />
      <MenuBar />

      <div className="desktop">
        {/* Desktop: draggable icons */}
        {!isMobile && (
          <>
            {sections.map((section, index) => (
              <DesktopIcon
                key={section.id}
                label={section.label}
                icon={section.icon}
                onDoubleClick={() => handleOpenWindow(section.id)}
                position={{ x: 20, y: 20 + index * 130 }}
              />
            ))}
            {notes.map(note => (
              <DesktopIcon
                key={note.id}
                label={note.label}
                icon={<PaperIcon />}
                onDoubleClick={() => handleOpenWindow(`note-${note.id}`)}
                onDragStart={() => handleNoteDragStart(note.id)}
                onDragEnd={handleNoteDragEnd}
                position={note.position}
              />
            ))}
            <DesktopIcon
              label="Trash"
              icon={<TrashIcon />}
              position={trashPosition}
              onDragEnd={(e, data) => setTrashPosition({ x: data.x, y: data.y })}
              onDoubleClick={() => {}}
            />
          </>
        )}

        {/* Open windows */}
        {Object.entries(openWindows).map(([id, position]) => {
          if (!position) return null;
          const topZ = Math.max(BASE_Z, ...Object.entries(openWindows)
            .filter(([, pos]) => pos)
            .map(([wid]) => zMap[wid] || BASE_Z));
          const winProps = {
            position,
            zIndex: zMap[id] || BASE_Z,
            focused: (zMap[id] || BASE_Z) === topZ,
            onFocus: () => focusWindow(id),
            onClose: () => handleCloseWindow(id),
          };

          if (id === 'text-editor') {
            return <TextEditor key={id} onSave={handleSaveNote} {...winProps} />;
          }

          if (id === 'chat') {
            return <Chat key={id} {...winProps} />;
          }

          if (id === 'terminal') {
            return (
              <Terminal
                key={id}
                {...winProps}
                onOpenApp={handleOpenWindow}
              />
            );
          }

          if (id.startsWith('note-')) {
            const note = notes.find(n => `note-${n.id}` === id);
            if (!note) return null;
            return (
              <Screen1 key={id} title={note.label} {...winProps}>
                <div className="note-content"><pre>{note.content}</pre></div>
              </Screen1>
            );
          }

          const section = sections.find(s => s.id === id);
          return (
            <Screen1 key={id} title={section.label} {...winProps}>
              {id === 'about' && <AboutContent image={seanImage} />}
              {id === 'resume' && <ResumeContent />}
              {id === 'projects' && <ProjectsContent />}
            </Screen1>
          );
        })}
      </div>

      {/* Mobile: bottom dock */}
      {isMobile && (
        <MobileDock items={dockItems} onOpen={handleOpenWindow} />
      )}
    </div>
  );
};

// ── About Me content ──────────────────────────────────────────────────────────
const AboutContent = ({ image }) => (
  <div className="about-content">
    <img src={image} alt="Sean" className="about-photo" />
    <div className="about-text">
      <p>Sean is a tech-savvy leader with a passion for solving complex problems and a track record of successfully driving application migrations, modernizations, and full stack development projects. With extensive experience as a Lead Software Engineer, Sean has a proven ability to lead cross-functional teams and collaborate with SMEs to develop effective solutions that meet business needs.</p>
      <p>He has a talent for navigating the entire software development lifecycle, from requirements gathering to production deployments, and possesses a wealth of experience in migrating applications from on-prem environments to modern cloud environments.</p>
      <p>Born and raised in New Orleans, Sean brings a unique perspective to his work. As an Eagle Scout, he developed strong leadership and problem-solving skills that he continues to apply in his professional life.</p>
      <p>When not working on software projects, Sean can often be found restoring classic video game consoles and 70s stereo equipment, combining his technical expertise with his appreciation for vintage electronics.</p>
    </div>
  </div>
);

// ── Resume content ────────────────────────────────────────────────────────────
const ResumeContent = () => (
  <div className="resume-content">

    {/* Header */}
    <div className="resume-header">
      <h1>SEAN SUTTON</h1>
      <p className="resume-title-line">Senior Software Engineer | Technical Lead</p>
      <p className="resume-contact-line">
        New Orleans, LA &nbsp;•&nbsp; 504-609-4452 &nbsp;•&nbsp; SeancSutton1@gmail.com &nbsp;•&nbsp;
        <a href="https://linkedin.com/in/seancsutton" target="_blank" rel="noreferrer">linkedin.com/in/seancsutton</a>
      </p>
    </div>

    {/* Summary */}
    <div className="resume-section">
      <h2>PROFESSIONAL SUMMARY</h2>
      <p>Results-driven Software Engineer and Technical Lead with 7+ years of experience delivering high-impact solutions across cloud migration, DevOps automation, and enterprise consulting. Proven track record of leading cross-functional teams through complex technical challenges, building custom tooling that improves deployment efficiency by 40%+, and architecting executive-level monitoring solutions. Active contributor to open-source AI/ML projects including GGML (precursor to Ollama), with hands-on experience in LLM quantization, semantic search, and hierarchical model architectures.</p>
    </div>

    {/* Skills */}
    <div className="resume-section">
      <h2>TECHNICAL SKILLS</h2>
      <div className="resume-skills-grid">
        <div>
          <h4>Languages &amp; Frameworks</h4>
          <p>JavaScript, Python, Node.js, React, Angular, Vue.js, Next.js, Bash</p>
        </div>
        <div>
          <h4>Cloud &amp; DevOps</h4>
          <p>AWS, Azure, GCP, Jenkins, CI/CD Pipelines, Infrastructure Automation</p>
        </div>
        <div>
          <h4>Databases</h4>
          <p>PostgreSQL, MySQL, MongoDB, Microsoft SQL Server, SQLite</p>
        </div>
        <div>
          <h4>AI/ML &amp; Tools</h4>
          <p>LLM Quantization, GGML, Semantic Search, Git, Veracode, Stratozone, SysTrack</p>
        </div>
      </div>
    </div>

    {/* Experience */}
    <div className="resume-section">
      <h2>PROFESSIONAL EXPERIENCE</h2>

      <div className="resume-job">
        <div className="resume-job-header">
          <span className="resume-employer">Contino / Cognizant</span>
          <span className="resume-dates">2022 – Present</span>
        </div>
        <p className="resume-job-title">DevOps Consultant &amp; Lead Software Engineer</p>

        <h4>Mortgage Company Client — Jenkins Automation Platform</h4>
        <ul>
          <li>Architected and developed a 2,000-line interactive TUI (Bash) to manage and automate the client's entire non-production Jenkins suite, enabling batch job execution with CSV-driven variable injection</li>
          <li>Implemented real-time pipeline progress monitoring and advanced error tracking, improving non-production deployment efficiency by 40%</li>
          <li>Delivered a reusable automation framework that reduced manual intervention and accelerated release cycles</li>
        </ul>

        <h4>Financial Institution Client — Executive Monitoring Dashboard</h4>
        <ul>
          <li>Designed and built a custom SysTrack dashboard for real-time executive support monitoring, synthesizing complex infrastructure metrics into actionable insights</li>
          <li>Developed weighted scoring formulas to produce generalized network health, device health, and application health scores for C-suite visibility</li>
        </ul>

        <h4>Grocery Retail Client — Production Crisis Resolution</h4>
        <ul>
          <li>Joined in-flight project to diagnose critical application failures in production; identified flawed testing routines that failed to account for peak throughput</li>
          <li>Implemented stress testing and queue management solutions, resolving issues that caused near-complete shutdown of client's sales capability</li>
        </ul>

        <h4>Retail Client — Application Portfolio Assessment</h4>
        <ul>
          <li>Led comprehensive application inventory audit and SME interviews; delivered 6R-style disposition recommendations for cloud migration strategy</li>
          <li>Upskilled on Google Stratozone/Stratoprobe assessment tools and delivered actionable assessment reports to client stakeholders</li>
        </ul>

        <h4>HR Technology Client — AWS Well-Architected Review</h4>
        <ul>
          <li>Developed custom AWS Well-Architected Review framework at organizational level, creating a reusable assessment model combining WAR with full organizational review</li>
          <li>Structured organization into functional pillars with crawl/walk/run maturity assignments; framework positioned as potential new service offering</li>
        </ul>
      </div>

      <div className="resume-job">
        <div className="resume-job-header">
          <span className="resume-employer">DXC Technology</span>
          <span className="resume-dates">2019 – 2022</span>
        </div>
        <p className="resume-job-title">Lead Software Engineer</p>
        <ul>
          <li>Led development teams through application migrations to new FMO environments, developing comprehensive test plans and coordinating SME sign-offs across development, ITG, staging, and production</li>
          <li>Built automated file system crawler to update server endpoints and cluster configurations, significantly reducing manual migration effort</li>
          <li>Directed Veracode vulnerability remediation initiatives for AWS cloud migrations; approach and plan templates adopted as models by other teams</li>
        </ul>
      </div>
    </div>

    {/* Open Source */}
    <div className="resume-section">
      <h2>OPEN SOURCE &amp; AI/ML CONTRIBUTIONS</h2>
      <ul>
        <li><strong>GGML Project Contributor</strong> — Core contributor to GGML, the foundational tensor library that preceded Ollama and enabled efficient local LLM inference</li>
        <li>Specialized in LLM quantization techniques for smaller models, optimizing memory footprint and inference speed</li>
        <li>Implemented semantic search models and experimented with hierarchical LLM architectures for improved reasoning capabilities</li>
      </ul>
    </div>

    {/* Education */}
    <div className="resume-section">
      <h2>EDUCATION</h2>
      <div className="resume-job-header">
        <span className="resume-employer">Operation Spark</span>
        <span className="resume-dates">2017 – 2018</span>
      </div>
      <p className="resume-job-title">Full Stack Software Engineering Immersion Program</p>
    </div>

  </div>
);

export default OS_Page;
